import { defineStore } from 'pinia'

import AnalyticsService from '~/libs/analytics-service'
import env from '~/libs/env'
import retryOnNetworkError from '~/libs/retry-on-network-error'
import {
  experiments,
  experimentWithId,
  resolveExperimentGroupFromCookie
} from '~/libs/experiments'

import { useRootStore } from '~/store/root'
import { useBorrowerStore } from '~/store/borrower'
import { useAffiliateCustomizationStore } from './affiliate-customization'
import { get } from 'es-toolkit/compat'
import { currentGeneration } from '~/libs/data-generation'


export const useExperimentsStore = defineStore('experiments', () => {
  const { $axios, $lendioCookies, $axiosWithRetry } = useNuxtApp()
  const rootStore = useRootStore()
  const borrowerStore = useBorrowerStore()
  const affiliateCustomizationStore = useAffiliateCustomizationStore()
  /*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
*/
  const activeUserExperiments = ref([])
  const anonymousExperiments = ref([])
  const loadedAnonymousExperiments = ref(false)

  const activeExperimentNames = ref([])
  const loadedActiveExperiments = ref(false)
  const experimentGroups = ref({})

/*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
*/


  const isTenantApplicationExperienceTestEnabled = computed(() => {
    return affiliateCustomizationStore.isLendioTenant === true && anonymousExperiments.value.some((ex) => ex?.experiment?.name === 'Tenant Application Experience V2' && ex?.group === 1)
  })

  const getExperimentGroupById = computed(() => {
    return (experimentId) => {
      return experimentGroups.value[experimentWithId(experimentId).name]
    }
  })

  const getExperimentGroupByExperimentName = computed(() => {
    return (name) => {
      return experimentGroups.value[name]
    }
  })

  const activeUserExperimentByName = computed(() => {
    return (name) => {
      const _activeUserExperiments = activeUserExperiments.value
      if (_activeUserExperiments?.length === 0) {
        return null
      }
      return _activeUserExperiments.find(experiment => {
        return experiment.name === name
      })
    }
  })

/*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
*/

  /**
   * Filter out tests that aren't active/don't exist
   * @param _experimentGroups
   * @returns {Promise<void>}
   */
  async function filterActiveExperimentGroupsByName(_experimentGroups) {
    return _experimentGroups.filter((group) =>
      activeExperimentNames.value.includes(group.name)
    )
  }

  async function getActiveExperimentsByName() {
    const res = await $axios.get(`${env('apiUrl')}/experiments/active`)

    activeExperimentNames.value = res.data.data.map(
      (experiment) => experiment.name
    )
  }

  /**
   * Collects all active user experiments and posts them to the api so they are
   * recorded in the database.
   */
  async function registerAllExperimentGroups() {
    const _experimentGroups = await this.resolveAllExperimentGroups()
    this.trackEventsForExperimentGroups(_experimentGroups)
    this.postExperimentGroupsToApi(_experimentGroups)
  }

  async function resolveExperimentGroup({
    experimentIdentifier,
    experimentGetterFn,
    cookieName,
  }) {
    // Check for group already in store and return it if available
    const groupInStore = getExperimentGroupById.value(experimentIdentifier)
    if (typeof groupInStore !== 'undefined') {
      return groupInStore
    }
    const group = await resolveExperimentGroupFromCookie(cookieName, $lendioCookies)

    const experiment = experimentGetterFn(experimentIdentifier)
    if (group === null) {
      return
    }
    experimentGroups.value[experiment.name] = group
    return group
  }

  /**
   * Parses the document's cookies to determine which experiment group a user belongs to
   * for a given experiment identifier. Checks cookies on an interval until a timeout is
   * reached.
   * @param {*} testIdentifier
   */
  async function resolveLendioExperimentGroup(experimentId) {
    return this.resolveExperimentGroup({
      experimentIdentifier: experimentId,
      experimentGetterFn: experimentWithId,
      cookieName: `lendio_exp_${experimentId}`
    })
  }

  async function resolveAllExperimentGroups() {
    const _experimentGroups = []
    await Promise.all(
      Object.keys(experiments).map(async (key) => {
        let experimentGroup = {
          id: experiments[key].id,
          name: experiments[key].name
        }
        if (experiments[key].id) {
          experimentGroup.group = await this.resolveLendioExperimentGroup(experiments[key].id)
        }
        if (experimentGroup.group > 0) {
          _experimentGroups.push(experimentGroup)
        }
      })
    )
    return _experimentGroups
  }

  async function postExperimentGroupsToApi(_experimentGroups) {
    // Ensure we are logged in
    if (
      !(
        rootStore.authUser &&
        rootStore.authUser.id
      )
    ) {
      return
    }

    // Only post experiment groups if we have at least one to send
    if (_experimentGroups.length <= 0) {
      return
    }

    // POST values of each that was not already eager loaded to the user experiments api
    // endpoint
    const url = `${env('apiUrl')}/user/${
      rootStore.authUser.id
    }/experiments`
    await $axios.post(url, JSON.stringify(_experimentGroups), {
      headers: {
        'Content-Type': 'application/json'
      }
    })
  }

  async function trackEventsForExperimentGroups(_experimentGroups) {
    _experimentGroups.forEach((experimentGroup) => {
      const groupName =
        experimentGroup.group === 1
          ? 'control group'
          : `variant group ${experimentGroup.group - 1}`
      const eventName = `Enter ${experimentGroup.name} ${groupName}`
      AnalyticsService.track(eventName)
    })
  }

  let _userExperimentsRequest = null
  let _userExperimentsFetched = { userId: null, generation: -1 }

  /**
   * Loads the current user's active experiments.
   *
   * cached returns the stored experiments when this user's were fetched at the current data
   * generation (libs/data-generation.js), and fetches otherwise.
   */
  async function fetchActiveExperimentsForCurrentUser({ cached = false } = {}) {
    const currentUserId = rootStore.userProfile.id ?? null
    if (!currentUserId) return;

    if (
      cached
      && _userExperimentsFetched.userId === currentUserId
      && _userExperimentsFetched.generation === currentGeneration()
    ) {
      return activeUserExperiments.value
    }

    if (!_userExperimentsRequest) {
      const generation = currentGeneration()
      _userExperimentsRequest = retryOnNetworkError(() => $axios.get(`${env("apiUrl")}/user/${currentUserId}/experiments/active`))
        .then(response => {
          const result = get(response, 'data.data', [])
          activeUserExperiments.value = result
          loadedActiveExperiments.value = true
          _userExperimentsFetched = { userId: currentUserId, generation }
          return result
        })
        .catch((e) => {
          if (e.message !== "Request failed with status code 401") {
            log.error("Unable to get user experiments", {
              err: e,
              errMessage: e.message,
              userId: currentUserId,
            })
          }
        })
    }

    try {
      return await _userExperimentsRequest
    } finally {
      _userExperimentsRequest = null
    }
  }

  async function assignExperimentsIfQualified(experiments) {
    const url = `${env('apiUrl')}/borrower/${borrowerStore.borrowerId}/experiments/assign-qualified`
    const assignedExperiments = await $axios.post(url, JSON.stringify({ experiments }))
      .catch(e => log.error('Unable to post assigned experiments', { err: e }))

    return assignedExperiments?.data?.data
  }

  function checkIfInActiveExperimentByName(name) {
    if (!name) {
      return false
    }

    return activeUserExperiments.value.some((userExperiment) => {
      return userExperiment.name === name && userExperiment.group >= 1
    })
  }

  async function getAnonymousExperiments(testIds = []) {
    const anonymousId = rootStore.anonymousId
    const tenantId = affiliateCustomizationStore.tenantId
    if (loadedAnonymousExperiments.value || !anonymousId) {
      return anonymousExperiments.value
    }
    const url = `${env('apiUrl')}/borrower-portal/anonymous/${anonymousId}/experiments`
    await $axiosWithRetry.get(url, { params: {
      testIds,
      tenantId
    } }).then((res) => {
      loadedAnonymousExperiments.value = true
      anonymousExperiments.value = res.data.data
    })
    .catch(e => log.error("Unable to get anonymous experiments", { err: e }))
  }

  async function connectAnonymousExperimentsToUser() {
    const _anonymousId = rootStore.getAnonymousId()
    const _borrowerId = borrowerStore.borrowerId
    if (!_borrowerId || !_anonymousId) {
      log.warning('connectAnonymousExperimentsToUser: missing borrowerId or anonymousId')
      return false
    }
    const url = `${env("apiUrl")}/borrower-portal/borrower/${_borrowerId}/anonymous/user-experiments`
    return await $axios.post(url, { anonymousId: _anonymousId }).then((res) => {
      return true
    }).catch((e) => {
      log.error('connectAnonymousExperimentsToUser: failed to connect anonymous experiments to user', { err: e, response: e.response })
      return false
    })
  }

  function reset() {
    activeUserExperiments.value = []
    loadedActiveExperiments.value = false
    _userExperimentsFetched = { userId: null, generation: -1 }
  }

  return {
    //STATE
    activeUserExperiments,
    activeExperimentNames,
    anonymousExperiments,
    loadedActiveExperiments,
    loadedAnonymousExperiments,
    experimentGroups,

    //GETTERS
    getExperimentGroupById,
    getExperimentGroupByExperimentName,
    activeUserExperimentByName,
    isTenantApplicationExperienceTestEnabled,

    //ACTIONS
    assignExperimentsIfQualified,
    connectAnonymousExperimentsToUser,
    filterActiveExperimentGroupsByName,
    registerAllExperimentGroups,
    getActiveExperimentsByName,
    getAnonymousExperiments,
    resolveExperimentGroup,
    resolveLendioExperimentGroup,
    resolveAllExperimentGroups,
    postExperimentGroupsToApi,
    trackEventsForExperimentGroups,
    fetchActiveExperimentsForCurrentUser,
    checkIfInActiveExperimentByName,
    reset,
  }
})
